/**
 * InlineMultiSelect Component
 * Compact multi-select rendered inline in the filter bar (no popover).
 * Applies on every change so filters take effect live.
 */

import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';

export interface InlineSelectOption {
  value: string;
  label: string;
}

export interface InlineMultiSelectProps {
  label: string;
  value: string[];
  options: InlineSelectOption[];
  onChange: (value: string[]) => void;
  minWidth?: number;
  maxWidth?: number;
  disabled?: boolean;
}

export const InlineMultiSelect: React.FC<InlineMultiSelectProps> = ({
  label,
  value,
  options,
  onChange,
  minWidth = 150,
  maxWidth = 240,
  disabled = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value;
    onChange(typeof next === 'string' ? next.split(',') : next);
  };

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <FormControl size="small" sx={{ minWidth, maxWidth }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        disabled={disabled}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          selected.length === 1 ? (
            labelFor(selected[0])
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {label}
              <Chip
                label={selected.length}
                size="small"
                color="primary"
                sx={{ height: 18, minWidth: 18, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Box>
          )
        }
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} dense>
            <Checkbox checked={value.indexOf(opt.value) > -1} size="small" />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
